import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { PhReceipt, PhClock } from 'ember-phosphor-icons';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import type { CourseCashTransaction } from 'tio-common/types/course-cash-transaction.ts';

interface ReceiptBannerSignature {
  Args: {
    transactions: CourseCashTransaction[];
    onUploadReceipt: (transaction: CourseCashTransaction) => void;
  };
  Element: HTMLElement;
}

export default class CourseCashReceiptBanner extends Component<ReceiptBannerSignature> {
  @service declare intl: IntlService;

  get unverifiedTransactions() {
    return this.args.transactions.filter((t) => t.receiptStatus === 'needed');
  }

  get transactionsByDueDate() {
    const groups = new Map<string, CourseCashTransaction[]>();

    this.unverifiedTransactions.forEach((transaction) => {
      const dueDate = transaction.receiptDueDate;
      if (!dueDate) return;

      if (!groups.has(dueDate)) {
        groups.set(dueDate, []);
      }
      groups.get(dueDate)!.push(transaction);
    });

    return Array.from(groups.entries())
      .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime())
      .map(([dueDate, transactions]) => ({
        dueDate,
        transactions: transactions.sort(
          (a, b) => new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime()
        ),
        daysUntilDue: this.calculateDaysUntilDue(dueDate),
      }));
  }

  calculateDaysUntilDue(dueDate: string) {
    // Parse dates in local timezone to avoid UTC conversion issues
    // Dates are in YYYY-MM-DD format from the backend
    const due = new Date(dueDate + 'T00:00:00');
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Reset to start of day for comparison

    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  formatDate = (dateString: string) => {
    // Parse in local timezone to avoid UTC date shift
    const date = new Date(dateString + 'T00:00:00');
    return this.intl.formatDate(date, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

  formatAmount = (amount: number) => {
    // Amount comes from transaction-adapter in cents
    // Convert to dollars for display
    return this.intl.formatNumber(amount / 100, {
      style: 'currency',
      currency: 'USD',
    });
  };

  isDaySingular = (days: number) => days === 1;

  formatDueDateText = (days: number) => {
    const absDays = Math.abs(days);
    const dayLabel = this.intl.t(
      absDays === 1 ? 'tas.course_cash.receipt_due.day' : 'tas.course_cash.receipt_due.days'
    );

    if (days < 0) {
      return this.intl.t('tas.course_cash.receipt_due.past_due', {
        days: absDays,
        dayLabel,
      });
    } else if (days === 0) {
      return this.intl.t('tas.course_cash.receipt_due.due_today');
    } else {
      return this.intl.t('tas.course_cash.receipt_due.due_in', {
        days,
        dayLabel,
      });
    }
  };

  <template>
    {{#if this.unverifiedTransactions.length}}
      <div class="bg-purple-100 border-purple-300 border-2 rounded-2xl p-4 mb-4" ...attributes>
        <div class="flex flex-col gap-1">
          {{! Header }}
          <div class="flex gap-2 items-center">
            <PhReceipt @size="20" @weight="regular" class="text-gray-900" />
            <p class="font-semibold text-gray-900 text-base leading-snug">
              {{t "tas.course_cash.receipt_banner.receipts_needed"}}
            </p>
          </div>

          {{! Description }}
          <p class="font-medium text-gray-700 text-base leading-relaxed">
            {{t "tas.course_cash.receipt_banner.description"}}
          </p>

          {{! Transactions by due date }}
          <div class="flex flex-col gap-3 pt-2">
            {{#each this.transactionsByDueDate as |group|}}
              <div class="flex flex-col gap-3">
                {{! Due date header }}
                <div class="flex gap-2 items-center">
                  <PhClock @size="24" @weight="regular" class="text-gray-900" />
                  <p class="font-bold text-gray-900 text-base leading-snug">
                    {{this.formatDueDateText group.daysUntilDue}}
                  </p>
                </div>

                {{! Transactions }}
                {{#each group.transactions as |transaction|}}
                  <div class="flex items-center justify-between w-full">
                    <div class="flex gap-6 items-center text-gray-900 text-base">
                      <p class="font-normal leading-snug">
                        {{this.formatDate transaction.transactionDate}}
                      </p>
                      <p class="font-normal leading-snug">
                        {{transaction.merchantName}}
                      </p>
                      <p class="font-medium leading-snug">
                        {{this.formatAmount transaction.amount}}
                      </p>
                      <button
                        type="button"
                        class="font-semibold underline cursor-pointer"
                        {{on "click" (fn @onUploadReceipt transaction)}}
                      >
                        {{t "tas.course_cash.receipt_banner.upload_receipt"}}
                      </button>
                    </div>
                  </div>
                {{/each}}
              </div>
            {{/each}}
          </div>
        </div>
      </div>
    {{/if}}
  </template>
}
